import { useCallback, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { useSearchParams } from "react-router-dom";
import { Globe2, MapPin } from "lucide-react";
import { getCommunityFilterFacets, searchCommunityPosts } from "../../services/api";
import Footer from "../../components/layout/Footer";
import CommunityHero from "../../components/community/home/CommunityHero";
import CommunityFilterBar from "../../components/community/home/CommunityFilterBar";
import TripSection from "../../components/community/home/TripSection";
import { CommunityError } from "../../errors/sentryErrors";
import { trackEvent } from "../../utils/analytics";
import { PAGE_CONTAINER } from "../../components/layout/pageWidth";

const PAGE_SIZE = 24;
const SEARCH_DELAY_MS = 450;

const EMPTY_FILTERS = { q: "", tripType: "", month: "", budget: "", duration: "" };

const readFilters = (params) => ({
  q: params.get("q") || "",
  tripType: params.get("tripType") || "",
  month: params.get("month") || "",
  budget: params.get("budget") || "",
  duration: params.get("duration") || "",
});

export default function CommunityHome() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState(() => readFilters(searchParams));
  const [sort, setSort] = useState(() => searchParams.get("sort") || "popular");
  // Draft text, separate from filters.q — committed a moment after typing stops
  // (or on Enter), so typing doesn't trigger a request per keystroke.
  const [queryDraft, setQueryDraft] = useState(() => searchParams.get("q") || "");

  const [posts, setPosts] = useState([]);
  const [facets, setFacets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = "Community | Romaya";
    trackEvent("community_home_viewed");
  }, []);

  // Facets drive the filter dropdowns; their labels are the values the search
  // API expects, so they are read from the server rather than hardcoded.
  useEffect(() => {
    let cancelled = false;
    getCommunityFilterFacets()
      .then((res) => !cancelled && setFacets(res))
      .catch((err) => {
        Sentry.withScope((scope) => {
          scope.setTag("feature", "community");
          scope.setTag("app", "web");
          scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
          Sentry.captureException(new CommunityError(err instanceof Error ? err.message : String(err)));
        });
        /* CommunityFilterBar falls back to static buckets. */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchPosts = useCallback(
    async (cursor = null) => {
      const isFirstPage = !cursor;
      isFirstPage ? setLoading(true) : setLoadingMore(true);
      if (isFirstPage) setError(null);

      try {
        const params = { ...filters, sort, limit: PAGE_SIZE };
        if (cursor) params.cursor = cursor;

        const res = await searchCommunityPosts(params);
        const batch = res.data || [];

        setPosts((prev) => (isFirstPage ? batch : [...prev, ...batch]));
        setNextCursor(res.meta?.nextCursor || null);
      } catch (err) {
        Sentry.withScope((scope) => {
          scope.setTag("feature", "community");
          scope.setTag("app", "web");
          scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
          Sentry.captureException(new CommunityError(err instanceof Error ? err.message : String(err)));
        });
        console.error("[Community] Failed to load posts:", err);
        // Only surface a first-page failure; a failed "load more" leaves the
        // already-rendered results intact and the button available to retry.
        if (isFirstPage) {
          setPosts([]);
          // A 400 means the filter combination was rejected — show the server's
          // reason so it is obvious which control is at fault.
          setError(
            err?.status === 400
              ? err.message || "That filter combination isn't valid."
              : "We couldn't load trips just now. Please try again.",
          );
        }
      } finally {
        isFirstPage ? setLoading(false) : setLoadingMore(false);
      }
    },
    [filters, sort],
  );

  // Refetch whenever the query changes, and mirror it into the URL so a
  // filtered view can be shared or reloaded.
  useEffect(() => {
    setNextCursor(null);
    fetchPosts(null);

    const next = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && next.set(k, v));
    if (sort !== "popular") next.set("sort", sort);
    setSearchParams(next, { replace: true });
    // setSearchParams is intentionally omitted — react-router returns a new
    // identity each render and including it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPosts]);

  const handleFilterChange = (key, value) => {
    trackEvent("community_filter_changed", { filter_key: key, filter_value: value });
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSortChange = (value) => {
    trackEvent("community_sort_changed", { sort_value: value });
    setSort(value);
  };

  const handleReset = () => {
    trackEvent("community_filters_reset");
    setQueryDraft("");
    setFilters(EMPTY_FILTERS);
  };

  const handleSearchSubmit = () => {
    const query = queryDraft.trim();
    trackEvent("community_search_submitted", { query });
    setFilters((prev) => ({ ...prev, q: query }));
  };

  // There is no Search button: the search starts by itself once typing has paused. Only a change in what was typed starts it —
  // the page opening with a query already in the address, or a reset, leaves it alone — and Enter (above) does not wait.
  const lastTyped = useRef(queryDraft);
  useEffect(() => {
    if (queryDraft === lastTyped.current) return undefined;
    lastTyped.current = queryDraft;
    const timer = setTimeout(() => {
      const query = queryDraft.trim();
      setFilters((prev) => {
        if (prev.q === query) return prev;
        trackEvent("community_search_submitted", { query, auto: true });
        return { ...prev, q: query };
      });
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [queryDraft]);

  const handleSearchClear = () => {
    setQueryDraft("");
    setFilters((prev) => ({ ...prev, q: "" }));
  };

  // `trip.isInternational` is computed server-side from destinationData.country
  // against the platform's home country. Trips with no country recorded fall
  // back to domestic so they stay visible.
  const domestic = posts.filter((p) => !p.trip?.isInternational);
  const international = posts.filter((p) => p.trip?.isInternational);

  const filterQuery = new URLSearchParams(
    Object.entries(filters).filter(([, v]) => v),
  ).toString();

  return (
    <div className="flex min-h-screen flex-col bg-white">
      {/* Navbar is fixed and grows from h-16 to h-20 at lg. */}
      <main className="flex-1 pt-16 lg:pt-20">
        <CommunityHero />

        <CommunityFilterBar
          filters={filters}
          onChange={handleFilterChange}
          onReset={handleReset}
          sort={sort}
          onSortChange={handleSortChange}
          facets={facets}
          searchValue={queryDraft}
          onSearchChange={setQueryDraft}
          onSearchSubmit={handleSearchSubmit}
          onSearchClear={handleSearchClear}
        />

        {error ? (
          <div className={`${PAGE_CONTAINER} py-20 text-center`}>
            <p className="mb-4 font-semibold text-gray-900">{error}</p>
            <button
              type="button"
              onClick={() => fetchPosts(null)}
              className="cursor-pointer rounded-full bg-teal-600 px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-teal-700"
            >
              Try again
            </button>
          </div>
        ) : (
          <>
            <TripSection
              icon={<MapPin size={20} strokeWidth={2.25} />}
              title="Domestic Trips"
              subtitle="Explore beautiful destinations within your country."
              posts={domestic}
              loading={loading}
              seeAllLabel="View All"
              seeAllTo={`/community/search${filterQuery ? `?${filterQuery}` : ""}`}
              emptyMessage="No domestic trips match these filters yet. Try clearing a filter, or be the first to publish one."
              tone="muted"
            />

            <TripSection
              icon={<Globe2 size={20} strokeWidth={2.25} />}
              title="International Trips"
              subtitle="Discover unforgettable international adventures."
              posts={international}
              loading={loading}
              seeAllLabel="View All"
              seeAllTo={`/community/search${filterQuery ? `?${filterQuery}` : ""}`}
              emptyMessage="No international trips match these filters yet. Try clearing a filter to see more."
            />

            {nextCursor && (
              <div className="flex justify-center pb-16">
                <button
                  type="button"
                  onClick={() => fetchPosts(nextCursor)}
                  disabled={loadingMore}
                  className="inline-flex cursor-pointer items-center gap-2 rounded-full border-2 border-teal-600 px-8 py-3 text-sm font-bold text-teal-700 transition-colors hover:bg-teal-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loadingMore ? "Loading…" : "Load More Plans"}
                </button>
              </div>
            )}
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}
